/**
 * THE ENDPOINT MAP — every registered route's authorization requirement, as data.
 *
 * Two rules give this table its value:
 *
 *   1. Every protected endpoint maps to EXACTLY ONE module action. One endpoint,
 *      one authorization decision. A route that needs two unrelated permissions
 *      is a route that should be split (see POST /users/:id/status).
 *
 *   2. A route absent from this table is DENIED, and a test walks the real
 *      Fastify route table and fails if any registered route is missing. Adding
 *      an endpoint therefore cannot silently create an unprotected surface — the
 *      build breaks until someone makes a deliberate, reviewable entry here.
 *
 * `legacy` records what the route required BEFORE the module layer, as data. That
 * is what shadow mode compares against to produce divergence reports, and it is
 * the field that gets deleted once enforcement is complete everywhere.
 *
 * Paths are Fastify patterns WITHOUT the `/v1` prefix, matched post-prefix, and
 * always the matched PATTERN rather than the raw URL — a crafted URL cannot be
 * made to resolve against a different entry.
 */
import type { ActionKey, ModuleKey } from "@yazamco/shared-types";
import { isKnownAction, moduleForAction } from "./authz.js";

export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

/**
 * What a route required before the module layer existed. Kept as data purely so
 * shadow mode can compute the legacy decision without the legacy guards being
 * mounted on the route.
 */
export type LegacyRequirement =
  | { kind: "public" }
  | { kind: "session" }
  | { kind: "role"; role: "Viewer" | "Operator" | "Administrator" }
  | { kind: "permission"; permission: string }
  | { kind: "service"; principal: string };

export type EndpointClassification =
  /** Requires a module action. The normal case. */
  | { kind: "module"; action: ActionKey }
  /** Deliberately reachable without authentication (probes, auth handshake). */
  | { kind: "public" }
  /** Authenticated, but authorizes nothing beyond "is a valid session": the
   *  response describes the caller themselves and nothing else. */
  | { kind: "self" }
  /** A non-human principal's own callback surface, authorized by that
   *  principal's identity and grants rather than by a human module level. */
  | { kind: "service"; principal: string; action?: ActionKey };

export interface EndpointMapping {
  method: HttpMethod;
  path: string;
  classify: EndpointClassification;
  legacy: LegacyRequirement;
  /** Why this route is classified as it is, where that is not self-evident. */
  note?: string;
  /**
   * `"always"` takes this route OUT of the shadow/enforce rollout: its module
   * decision governs whatever the deployment mode is, and `legacy` is recorded
   * but never consulted for it.
   *
   * Only correct for a surface that is NEW in the module model — one whose
   * `legacy` entry describes an authority nobody actually held through the
   * product. Shadow mode exists to prove the module layer reproduces the legacy
   * model before it replaces it; where there is no legacy behaviour to
   * reproduce, there is nothing to prove, and deferring to the legacy role
   * means the granular permission is decorative. Setting this on a route that
   * DID have a legacy equivalent would skip exactly the verification the
   * rollout is for, so it stays a per-route opt-in with a stated reason rather
   * than a module-wide switch.
   */
  enforce?: "always";
}

const mod = (action: ActionKey): EndpointClassification => ({
  kind: "module",
  action,
});
const role = (r: "Viewer" | "Operator" | "Administrator"): LegacyRequirement => ({
  kind: "role",
  role: r,
});
const perm = (permission: string): LegacyRequirement => ({
  kind: "permission",
  permission,
});

export const ENDPOINT_MAP: readonly EndpointMapping[] = [
  // ══ Unauthenticated: probes and the authentication handshake ═══════════════
  { method: "GET", path: "/health/live", classify: { kind: "public" }, legacy: { kind: "public" } },
  { method: "GET", path: "/health/ready", classify: { kind: "public" }, legacy: { kind: "public" } },
  { method: "POST", path: "/auth/login", classify: { kind: "public" }, legacy: { kind: "public" } },
  { method: "POST", path: "/auth/refresh", classify: { kind: "public" }, legacy: { kind: "public" } },
  { method: "POST", path: "/auth/logout", classify: { kind: "public" }, legacy: { kind: "public" } },
  { method: "GET", path: "/auth/entra/login", classify: { kind: "public" }, legacy: { kind: "public" } },
  { method: "GET", path: "/auth/entra/callback", classify: { kind: "public" }, legacy: { kind: "public" } },
  { method: "GET", path: "/auth/entra/logout", classify: { kind: "public" }, legacy: { kind: "public" } },

  // ══ Self: describes the caller, authorizes nothing else ═══════════════════
  {
    method: "GET",
    path: "/auth/me",
    classify: { kind: "self" },
    legacy: { kind: "session" },
  },
  {
    method: "GET",
    path: "/me",
    classify: { kind: "self" },
    legacy: { kind: "session" },
    note:
      "Returns the caller's identity, effective access and caller-scoped " +
      "preferences (including the polling cadence). Deliberately not gated by " +
      "the settings module: a principal must be able to read its own session.",
  },

  // ══ Dashboard ═════════════════════════════════════════════════════════════
  { method: "GET", path: "/dashboard/summary", classify: mod("dashboard.read"), legacy: role("Viewer") },
  { method: "GET", path: "/dashboard/health", classify: mod("dashboard.read"), legacy: role("Viewer") },
  { method: "GET", path: "/dashboard/overview", classify: mod("dashboard.read"), legacy: role("Viewer") },

  // ══ Customers ═════════════════════════════════════════════════════════════
  { method: "GET", path: "/customers", classify: mod("customers.read"), legacy: role("Viewer") },
  { method: "GET", path: "/customers/service-catalog", classify: mod("customers.read"), legacy: role("Viewer") },
  { method: "GET", path: "/customers/:customerId/services", classify: mod("customers.read"), legacy: role("Viewer") },
  { method: "POST", path: "/customers/upsert", classify: mod("customers.upsert"), legacy: role("Operator") },
  { method: "POST", path: "/customers/bulk-upsert", classify: mod("customers.bulk_import"), legacy: role("Operator") },
  {
    method: "GET",
    path: "/customers/jobs/:jobId",
    classify: mod("customers.read_provisioning_secret"),
    legacy: role("Operator"),
    note: "The job body carries the one-time credentials created for the customer.",
  },
  { method: "POST", path: "/customers/email-result", classify: mod("customers.email_credentials"), legacy: role("Operator") },
  {
    method: "GET",
    path: "/customers/email-history",
    classify: mod("customers.email_credentials"),
    legacy: role("Operator"),
    note: "Who past results were mailed to. Same action as sending: the recipient list of a credential mail is as sensitive as the send.",
  },
  {
    method: "POST",
    path: "/customers/jobs/:jobId/result",
    classify: { kind: "service", principal: "svc_n8n_provisioning", action: "customers.provisioning_callback" },
    legacy: { kind: "service", principal: "n8n shared secret" },
    note: "Provisioning callback. Authorized as its own principal, not as a human.",
  },

  // ══ Service catalog ═══════════════════════════════════════════════════════
  { method: "GET", path: "/services", classify: mod("services.read"), legacy: perm("services.read") },
  { method: "GET", path: "/services/:id", classify: mod("services.read"), legacy: perm("services.read") },
  { method: "POST", path: "/services", classify: mod("services.create"), legacy: perm("services.manage") },
  { method: "PATCH", path: "/services/:id", classify: mod("services.update"), legacy: perm("services.manage") },
  { method: "DELETE", path: "/services/:id", classify: mod("services.delete"), legacy: perm("services.manage") },

  // ══ Servers ═══════════════════════════════════════════════════════════════
  { method: "GET", path: "/servers", classify: mod("servers.read"), legacy: role("Viewer") },
  { method: "GET", path: "/servers/:id/pulseway", classify: mod("servers.read"), legacy: role("Viewer") },
  { method: "POST", path: "/servers", classify: mod("servers.create"), legacy: role("Operator") },
  { method: "PUT", path: "/servers/:id", classify: mod("servers.update"), legacy: role("Operator") },
  { method: "POST", path: "/servers/refresh", classify: mod("servers.refresh"), legacy: role("Operator") },
  { method: "POST", path: "/servers/:id/refresh", classify: mod("servers.refresh"), legacy: role("Operator") },
  { method: "POST", path: "/servers/pulseway/refresh", classify: mod("servers.refresh"), legacy: role("Operator") },
  { method: "POST", path: "/servers/:id/pulseway/refresh-updates", classify: mod("servers.refresh"), legacy: role("Operator") },
  { method: "POST", path: "/servers/tags/rename", classify: mod("servers.manage_tags"), legacy: role("Operator") },
  { method: "POST", path: "/servers/tags/delete", classify: mod("servers.manage_tags"), legacy: role("Operator") },
  { method: "DELETE", path: "/servers/:id", classify: mod("servers.delete"), legacy: role("Administrator") },

  // ══ Containers ════════════════════════════════════════════════════════════
  { method: "GET", path: "/containers", classify: mod("containers.read"), legacy: role("Viewer") },
  { method: "GET", path: "/containers/metadata", classify: mod("containers.read"), legacy: role("Viewer") },
  { method: "GET", path: "/containers/:containerId/history", classify: mod("containers.read"), legacy: role("Viewer") },
  { method: "PUT", path: "/containers/metadata/:scope/:key", classify: mod("containers.classify"), legacy: role("Operator") },
  { method: "POST", path: "/containers/refresh", classify: mod("containers.refresh"), legacy: role("Operator") },
  { method: "POST", path: "/containers/servers/:serverId/refresh", classify: mod("containers.refresh"), legacy: role("Operator") },

  // ══ Jenkins ═══════════════════════════════════════════════════════════════
  { method: "GET", path: "/jenkins/jobs", classify: mod("jenkins.read"), legacy: role("Viewer") },
  { method: "POST", path: "/jenkins/trigger", classify: mod("jenkins.trigger"), legacy: role("Operator") },

  // ══ Billing & License Reconciliation ══════════════════════════════════════
  // `legacy` is role("Administrator") throughout, and that is a statement about
  // shadow mode rather than about the module: this module is NEW, so there is no
  // pre-existing requirement to compare against. Naming the most restrictive legacy
  // role means shadow mode reports a divergence only where the module layer would
  // DENY someone an administrator could reach — the direction worth hearing about —
  // instead of inventing a permissive baseline that never existed.
  //
  // Billing is admin-only by decision: no other system role holds the module at any
  // level, so no read here may declare a laxer baseline (see system-role-grants.ts).
  { method: "POST", path: "/billing/sku-imports", classify: mod("billing.upload"), legacy: role("Administrator") },
  { method: "GET", path: "/billing/sku-imports", classify: mod("billing.read"), legacy: role("Administrator") },
  { method: "GET", path: "/billing/sku-imports/:batchId", classify: mod("billing.read"), legacy: role("Administrator") },
  { method: "GET", path: "/billing/sku-imports/:batchId/rows", classify: mod("billing.read"), legacy: role("Administrator") },
  { method: "GET", path: "/billing/sku-imports/:batchId/changes", classify: mod("billing.read"), legacy: role("Administrator") },
  {
    method: "POST",
    path: "/billing/sku-imports/:batchId/approve",
    classify: mod("billing.import_approve"),
    legacy: role("Administrator"),
    note:
      "Applies a staged price list to the catalogue every future invoice is " +
      "priced from. Deliberately a different action from the upload that staged it.",
  },
  { method: "POST", path: "/billing/sku-imports/:batchId/discard", classify: mod("billing.upload"), legacy: role("Administrator") },
  {
    method: "POST",
    path: "/billing/sku-imports/:batchId/rollback",
    classify: mod("billing.import_approve"),
    legacy: role("Administrator"),
    note: "Reverses an applied import; the same authority as approving one.",
  },
  { method: "GET", path: "/billing/skus", classify: mod("billing.read"), legacy: role("Administrator") },
  { method: "GET", path: "/billing/skus/:skuId", classify: mod("billing.read"), legacy: role("Administrator") },
  // Maintaining the catalogue by hand — ordinary `edit` work, through
  // `billing.mappings_manage`, and deliberately NOT the special
  // `billing.import_approve`.
  //
  // import_approve exists for applying a whole staged PRICE LIST: a bulk diff
  // over the catalogue, reviewed before it lands. Typing one SKU is a different
  // act, and it is the same act as the mapping work it unblocks — the reason to
  // add one by hand is that a licence cannot be mapped until the SKU exists, so
  // gating it above mapping makes the workflow impossible to finish for exactly
  // the person doing it. It is also the gate on setting a licence's display
  // name, which is written straight into the Priority charge row; a SKU code
  // written into the same row cannot sensibly require more authority than that.
  {
    method: "POST",
    path: "/billing/skus",
    classify: mod("billing.mappings_manage"),
    legacy: role("Administrator"),
    note: "Creates one catalogue item directly. A duplicate מק_ט is refused, never merged.",
  },
  {
    method: "PATCH",
    path: "/billing/skus/:skuId",
    classify: mod("billing.mappings_manage"),
    legacy: role("Administrator"),
    note:
      "Edits a catalogue item in place. Mappings and import history reference the " +
      "SKU by internal id, so nothing here can cost a mapping — including a rename.",
  },
  {
    method: "DELETE",
    path: "/billing/skus/:skuId",
    classify: mod("billing.mappings_manage"),
    legacy: role("Administrator"),
    note:
      "Deletes a catalogue item outright. The same authority as creating and " +
      "editing one, because the destructive case is not authorization but " +
      "reference: the API refuses (409) while any mapping, applied import " +
      "change or billing-run resolution still names the item, so what this " +
      "action can actually remove is a row nothing depends on.",
  },
  { method: "GET", path: "/billing/skus/:skuId/history", classify: mod("billing.read"), legacy: role("Administrator") },
  { method: "GET", path: "/billing/license-names", classify: mod("billing.read"), legacy: role("Administrator") },
  { method: "GET", path: "/billing/license-names/:licenseNameId", classify: mod("billing.read"), legacy: role("Administrator") },
  {
    method: "PUT",
    path: "/billing/license-names/:licenseNameId/display-name",
    classify: mod("billing.mappings_manage"),
    legacy: role("Administrator"),
    note:
      "Renames a licence for every billing screen AND for the Priority charge " +
      "row it prints on — the same authority as deciding which SKU it bills to.",
  },
  // A rebuild deletes unmapped licence-name rows, so it is mappings_manage and
  // not billing.read — it changes the worklist, it does not report on it.
  { method: "POST", path: "/billing/license-names/rebuild", classify: mod("billing.mappings_manage"), legacy: role("Administrator") },
  { method: "POST", path: "/billing/mappings", classify: mod("billing.mappings_manage"), legacy: role("Administrator") },
  { method: "DELETE", path: "/billing/mappings/:mappingId", classify: mod("billing.mappings_manage"), legacy: role("Administrator") },

  // Monthly API snapshots (phase 6). Listing/reading is billing.read; starting a
  // collection is the special action already granted admin-only in
  // system-role-grants.ts (billing.snapshot_run) — it reaches four vendor APIs
  // with our own credentials, the same authority class as fortigate.sweep_run.
  { method: "GET", path: "/billing/snapshots/sources", classify: mod("billing.read"), legacy: role("Administrator") },
  { method: "GET", path: "/billing/snapshots", classify: mod("billing.read"), legacy: role("Administrator") },
  { method: "GET", path: "/billing/snapshots/:snapshotId", classify: mod("billing.read"), legacy: role("Administrator") },
  {
    method: "POST",
    path: "/billing/snapshots",
    classify: mod("billing.snapshot_run"),
    legacy: role("Administrator"),
    note: "Claims one (source, billingMonth) slot and collects from a single vendor API.",
  },
  {
    method: "POST",
    path: "/billing/snapshots/run-all",
    classify: mod("billing.snapshot_run"),
    legacy: role("Administrator"),
    note: "Same authority as the single-source run, applied to every configured source.",
  },
  {
    method: "POST",
    path: "/billing/snapshots/:snapshotId/unseal",
    classify: mod("billing.snapshot_run"),
    legacy: role("Administrator"),
    note:
      "Releases a closed snapshot's (source, month) slot so the month can be collected " +
      "again. Deliberately NOT a new special action: it is only reachable as the first " +
      "half of re-collecting, and anyone holding billing.snapshot_run can already spend " +
      "our vendor credentials on that month — what unsealing adds is that the previous " +
      "snapshot stops being an exportable source, which is strictly less authority than " +
      "the export action itself.",
  },

  // 365/CSP uploads (phase 7, restaged in Postgres in phase 8). "upload"
  // already covers stage-a-batch work for the SKU importer; approving/
  // discarding an upload batch is the same authority class, so no new
  // special action is declared for it.
  { method: "POST", path: "/billing/uploads", classify: mod("billing.upload"), legacy: role("Administrator") },
  { method: "GET", path: "/billing/uploads", classify: mod("billing.read"), legacy: role("Administrator") },
  { method: "GET", path: "/billing/uploads/:batchId", classify: mod("billing.read"), legacy: role("Administrator") },
  { method: "GET", path: "/billing/uploads/:batchId/rows", classify: mod("billing.read"), legacy: role("Administrator") },
  {
    method: "POST",
    path: "/billing/uploads/:batchId/approve",
    classify: mod("billing.upload"),
    legacy: role("Administrator"),
    note: "Persists the staged records to billing_records; the same authority as staging the upload.",
  },
  { method: "POST", path: "/billing/uploads/:batchId/discard", classify: mod("billing.upload"), legacy: role("Administrator") },

  // The customer directory, billing-scoped. Same data as GET /customers, read
  // under billing.read: the picker that resolves a customer finding needs it,
  // and requiring customers.read for it made a workflow billing grants in full
  // impossible to finish. Run detail already names every customer in the run,
  // so this discloses nothing billing.read did not already show.
  { method: "GET", path: "/billing/customers", classify: mod("billing.read"), legacy: role("Administrator") },

  // Billing run reconciliation (phase 8). Combining sources, validating and
  // resolving a run is the same edit-level authority as staging an upload;
  // no new special action is declared for it.
  { method: "GET", path: "/billing/runs/sources", classify: mod("billing.read"), legacy: role("Administrator") },
  { method: "POST", path: "/billing/runs", classify: mod("billing.upload"), legacy: role("Administrator") },
  { method: "GET", path: "/billing/runs", classify: mod("billing.read"), legacy: role("Administrator") },
  { method: "GET", path: "/billing/runs/:runId", classify: mod("billing.read"), legacy: role("Administrator") },
  { method: "POST", path: "/billing/runs/:runId/validate", classify: mod("billing.upload"), legacy: role("Administrator") },
  { method: "POST", path: "/billing/runs/:runId/resolve-customer", classify: mod("billing.upload"), legacy: role("Administrator") },
  { method: "POST", path: "/billing/runs/:runId/resolve-sku", classify: mod("billing.upload"), legacy: role("Administrator") },
  {
    method: "POST",
    path: "/billing/runs/:runId/resolve-prorata-span",
    classify: mod("billing.upload"),
    legacy: role("Administrator"),
  },
  { method: "POST", path: "/billing/runs/:runId/discard", classify: mod("billing.upload"), legacy: role("Administrator") },

  // The run's customers: who is ready, who is blocked and why, who has already
  // been exported. A read, and billing.read — the run detail already names every
  // customer in the run, so grouping them discloses nothing new.
  {
    method: "GET",
    path: "/billing/runs/:runId/customers",
    classify: mod("billing.read"),
    legacy: role("Administrator"),
  },

  // Priority export (phase 9). The ONE run endpoint that is not billing.upload:
  // `billing.export` is a separate special action precisely so that producing
  // the file every invoice is raised from can be granted independently of
  // reconciling the data behind it — the same distinction reports.export draws
  // against reports.generate.
  {
    method: "GET",
    path: "/billing/runs/:runId/export",
    classify: mod("billing.export"),
    legacy: role("Administrator"),
    note:
      "Downloads the priority-v1 import file for every customer that is ready " +
      "and not yet exported. Re-derives eligibility from live data on every " +
      "call; never trusts the run's stored blocking count.",
  },
  {
    method: "POST",
    path: "/billing/runs/:runId/export",
    classify: mod("billing.export"),
    legacy: role("Administrator"),
    note:
      "The same file for a NAMED set of customers, and the only way to export a " +
      "customer this run has already billed. Same authority as the GET — it is " +
      "the same artifact; a POST because re-exporting must not be something a " +
      "browser refresh can do.",
  },

  // ══ Reports ═══════════════════════════════════════════════════════════════
  { method: "GET", path: "/reports/systems", classify: mod("reports.read"), legacy: role("Viewer") },
  { method: "GET", path: "/reports/companies", classify: mod("reports.read"), legacy: role("Viewer") },
  { method: "GET", path: "/reports/templates", classify: mod("reports.read"), legacy: role("Viewer") },
  { method: "POST", path: "/reports/generate", classify: mod("reports.generate"), legacy: role("Operator") },
  {
    method: "POST",
    path: "/reports/export",
    classify: mod("reports.export"),
    legacy: role("Operator"),
    note:
      "Server-side export so the capability is separately grantable. Generating " +
      "renders on screen; exporting produces a file that leaves the platform.",
  },
  { method: "POST", path: "/reports/templates", classify: mod("reports.template_create"), legacy: role("Operator") },
  { method: "DELETE", path: "/reports/templates/:id", classify: mod("reports.template_delete"), legacy: role("Operator") },

  // ══ Customer audits ═══════════════════════════════════════════════════════
  { method: "GET", path: "/customer-audits/schedule", classify: mod("audits.read"), legacy: role("Viewer") },
  { method: "GET", path: "/customer-audits/pulseway/sync", classify: mod("audits.read"), legacy: role("Viewer") },
  { method: "GET", path: "/customer-audits/runs", classify: mod("audits.read"), legacy: role("Viewer") },
  { method: "GET", path: "/customer-audits/runs/:runId", classify: mod("audits.read"), legacy: role("Viewer") },
  { method: "GET", path: "/customer-audits/batches/:batchId", classify: mod("audits.read"), legacy: role("Viewer") },
  { method: "PATCH", path: "/customer-audits/schedule/settings", classify: mod("audits.schedule_manage"), legacy: role("Operator") },
  { method: "PUT", path: "/customer-audits/schedule/subscriptions/:customerId", classify: mod("audits.schedule_manage"), legacy: role("Operator") },
  { method: "DELETE", path: "/customer-audits/schedule/subscriptions/:customerId", classify: mod("audits.schedule_manage"), legacy: role("Operator") },
  { method: "POST", path: "/customer-audits/schedule/subscriptions/bulk", classify: mod("audits.bulk_subscribe"), legacy: role("Operator") },
  { method: "POST", path: "/customer-audits/schedule/run-now", classify: mod("audits.schedule_run_now"), legacy: role("Operator") },
  { method: "POST", path: "/customer-audits/pulseway/sync", classify: mod("audits.pulseway_sync"), legacy: role("Operator") },
  { method: "POST", path: "/customer-audits/runs", classify: mod("audits.run"), legacy: role("Operator") },
  { method: "POST", path: "/customer-audits/runs/:runId/retry", classify: mod("audits.retry"), legacy: role("Operator") },
  { method: "POST", path: "/customer-audits/runs/:runId/email", classify: mod("audits.email_report"), legacy: role("Operator") },
  { method: "GET", path: "/customer-audits/runs/:runId/report.html", classify: mod("audits.export_report"), legacy: role("Viewer") },
  { method: "GET", path: "/customer-audits/runs/:runId/report.pdf", classify: mod("audits.export_report"), legacy: role("Viewer") },

  // ══ Microsoft 365 Copilot ═════════════════════════════════════════════════
  { method: "POST", path: "/copilot/chat", classify: mod("copilot.chat"), legacy: role("Viewer") },
  { method: "POST", path: "/copilot/chat/stream", classify: mod("copilot.chat"), legacy: role("Viewer") },
  { method: "POST", path: "/copilot/suggestions", classify: mod("copilot.chat"), legacy: role("Viewer") },
  // Saved sessions and per-user settings. Every one of these routes is scoped
  // to the CALLER's own rows by the service, so they need no permission beyond
  // "may use Copilot" — there is no cross-user read here to gate.
  // Choosing what to ask against is part of asking, so the site listing rides
  // on `copilot.chat` rather than forming a privilege of its own. Graph
  // security-trims it to the caller regardless.
  { method: "GET", path: "/copilot/knowledge/sites", classify: mod("copilot.chat"), legacy: role("Viewer") },
  { method: "GET", path: "/copilot/knowledge/tree", classify: mod("copilot.chat"), legacy: role("Viewer") },
  // The retrieval self-test. Classified with chat rather than as an admin tool:
  // it runs the caller's own question on the caller's own delegated token and
  // can reach nothing they could not already ask for, and the person who needs
  // to know why a scoped question came back empty is the person who asked it.
  { method: "POST", path: "/copilot/knowledge/probe", classify: mod("copilot.chat"), legacy: role("Viewer") },
  // Attaching a file from the caller's own computer. `copilot.chat` for the
  // same reason as the routes above — it is part of asking a question, and it
  // writes only into the caller's own OneDrive app folder on the caller's own
  // delegated token, so it can reach nothing they could not reach themselves.
  { method: "POST", path: "/copilot/files", classify: mod("copilot.chat"), legacy: role("Viewer") },
  // Fetching an image the answer embedded. `copilot.chat` again, and for the
  // narrowest reason available: it reads one Microsoft-hosted object that the
  // answer in front of the caller already points at, on the caller's own
  // delegated token, and can reach no host outside the allowlist in
  // `COPILOT_IMAGE_HOSTS`.
  { method: "GET", path: "/copilot/images", classify: mod("copilot.chat"), legacy: role("Viewer") },
  // MCP business tools. Every one of these rides `copilot.chat` for the same
  // reason the knowledge-scope routes do: choosing what to ground a question in
  // is part of asking it, not a privilege of its own, and none of them is
  // useful to a caller who cannot chat. Each is a READ against a system the
  // operator registered — no write path exists — made under the CALLER's own
  // credential, and owner-scoped by the connection, preference and invocation
  // rows it touches, which is the same shape as every other route here.
  { method: "GET", path: "/copilot/mcp/integrations", classify: mod("copilot.chat"), legacy: role("Viewer") },
  { method: "GET", path: "/copilot/mcp/integrations/:id/tools", classify: mod("copilot.chat"), legacy: role("Viewer") },
  // Connecting your OWN account, and choosing which of your own tools Copilot
  // may read. Both are self-scoped by construction: the connection row is keyed
  // on the caller, the preference row is keyed on the caller, and there is no
  // body on any of these routes capable of naming an endpoint, an auth
  // strategy, a tool allowlist or a read-only policy — those live in the global
  // catalog, which only the `/copilot/mcp/admin/*` routes below can write. So
  // this is not integration ADMINISTRATION and is not classified as such: the
  // global editor exists now and it carries its own action, because that one
  // really does decide what everybody's questions can reach.
  // The composer's one switch: may Copilot read this business system for me.
  // It names no tool, and it cannot widen anything — the service resolves "on"
  // against the global allowlist and live discovery.
  { method: "PATCH", path: "/copilot/mcp/integrations/:id/tools", classify: mod("copilot.chat"), legacy: role("Viewer") },
  { method: "PATCH", path: "/copilot/mcp/integrations/:id/tools/:toolName", classify: mod("copilot.chat"), legacy: role("Viewer") },
  { method: "GET", path: "/copilot/mcp/integrations/:id/oauth/start", classify: mod("copilot.chat"), legacy: role("Viewer") },
  // The provider's callback. AUTHENTICATED like every other route here — the
  // session cookie is SameSite=Lax and so travels on the provider's top-level
  // GET — and deliberately not `public`: the state row names the user who
  // started the flow, and comparing it against the signed-in caller is half of
  // the user binding. A public callback would have only the state value to go
  // on, which is a secret in a URL.
  { method: "GET", path: "/copilot/mcp/oauth/callback", classify: mod("copilot.chat"), legacy: role("Viewer") },
  { method: "POST", path: "/copilot/mcp/integrations/:id/connection", classify: mod("copilot.chat"), legacy: role("Viewer") },
  { method: "DELETE", path: "/copilot/mcp/integrations/:id/connection", classify: mod("copilot.chat"), legacy: role("Viewer") },
  { method: "POST", path: "/copilot/mcp/retrieve", classify: mod("copilot.chat"), legacy: role("Viewer") },

  // ── The GLOBAL MCP catalog, administered ─────────────────────────────────
  // A separate action, and the separation is the point of migration 0053. Every
  // route above is one person acting on their own connection to a business
  // system somebody else approved. Every route below decides, for EVERYBODY,
  // which business systems exist, at which endpoint, under which OAuth
  // application credentials, with which tools allowlisted — which is to say it
  // decides what data can be placed in front of a language model on this
  // deployment. A Copilot user, including one at `full`, holds none of it.
  //
  // `copilot.mcp_integrations_read` is not a courtesy tier: the listing names
  // every endpoint host and every OAuth client id, which is exactly what an
  // auditor should be able to read and exactly what they should not be able to
  // re-point. `manage` implies it — see SPECIAL_ACTION_IMPLIES in the manifest,
  // which is what makes that true of the guard and the UI alike — never the
  // other way round.
  //
  // EVERY route below is `enforce: "always"`, so the two actions decide access
  // in shadow mode as well as under enforcement. This surface is new in the
  // module model: before migration 0053 it WAS a deployment — an env edit and a
  // container restart — so the `role("Administrator")` recorded as `legacy` is
  // not an authority anyone held through the product, it is the closest
  // approximation of "whoever could deploy". Deferring to it would mean the
  // granular actions gated nothing but the visibility of a screen, while any
  // Administrator kept the API regardless of what was granted. The legacy
  // values stay as data so the divergence report still shows who the move
  // changes, and shadow/enforce is untouched for every other route.
  { method: "GET", path: "/copilot/mcp/admin/integrations", classify: mod("copilot.mcp_integrations_read"), legacy: role("Administrator"), enforce: "always" },
  { method: "GET", path: "/copilot/mcp/admin/integrations/:id", classify: mod("copilot.mcp_integrations_read"), legacy: role("Administrator"), enforce: "always" },
  { method: "POST", path: "/copilot/mcp/admin/integrations", classify: mod("copilot.mcp_integrations_manage"), legacy: role("Administrator"), enforce: "always" },
  { method: "PATCH", path: "/copilot/mcp/admin/integrations/:id", classify: mod("copilot.mcp_integrations_manage"), legacy: role("Administrator"), enforce: "always" },
  { method: "DELETE", path: "/copilot/mcp/admin/integrations/:id", classify: mod("copilot.mcp_integrations_manage"), legacy: role("Administrator"), enforce: "always" },
  // Test and discovery are classified as MANAGE rather than read, and that is
  // deliberate: both make an outbound call to an address named in the request's
  // own row, under the calling administrator's personal connection. A reader
  // who could trigger one would hold a request-origination primitive against a
  // destination a manager chose — bounded by the same SSRF checks as every
  // other MCP call, but still not something "may view the catalog" should buy.
  {
    method: "POST",
    path: "/copilot/mcp/admin/integrations/:id/test",
    classify: mod("copilot.mcp_integrations_manage"),
    legacy: role("Administrator"),
    enforce: "always",
    note: "Makes an outbound call to the configured endpoint under the caller's own connection.",
  },
  {
    method: "POST",
    path: "/copilot/mcp/admin/integrations/:id/discover-tools",
    classify: mod("copilot.mcp_integrations_manage"),
    legacy: role("Administrator"),
    enforce: "always",
    note: "Live tools/list against the configured endpoint under the caller's own connection.",
  },
  { method: "PUT", path: "/copilot/mcp/admin/integrations/:id/allowed-tools", classify: mod("copilot.mcp_integrations_manage"), legacy: role("Administrator"), enforce: "always" },
  // The OAuth callback URL an administrator must register with the provider.
  // Read-classified and secret-free: it is derived from this deployment's
  // trusted public-origin configuration, never from a request Host header.
  { method: "GET", path: "/copilot/mcp/admin/oauth-callback-url", classify: mod("copilot.mcp_integrations_read"), legacy: role("Administrator"), enforce: "always" },
  // `_manage` and not `_read`, although it writes no row. It makes this
  // deployment dial a host named in the request body, and it is the step that
  // produces the OAuth configuration somebody is about to save — the same
  // reasoning that puts `test` and `discover-tools` on the management action.
  { method: "POST", path: "/copilot/mcp/admin/oauth-discovery", classify: mod("copilot.mcp_integrations_manage"), legacy: role("Administrator"), enforce: "always" },
  { method: "GET", path: "/copilot/conversations", classify: mod("copilot.history"), legacy: role("Viewer") },
  { method: "POST", path: "/copilot/conversations", classify: mod("copilot.history"), legacy: role("Viewer") },
  { method: "GET", path: "/copilot/conversations/:id", classify: mod("copilot.history"), legacy: role("Viewer") },
  { method: "PATCH", path: "/copilot/conversations/:id", classify: mod("copilot.history"), legacy: role("Viewer") },
  { method: "DELETE", path: "/copilot/conversations/:id", classify: mod("copilot.history"), legacy: role("Viewer") },
  { method: "GET", path: "/copilot/settings", classify: mod("copilot.settings"), legacy: role("Viewer") },
  { method: "PATCH", path: "/copilot/settings", classify: mod("copilot.settings"), legacy: role("Viewer") },
  { method: "GET", path: "/copilot/prompts", classify: mod("copilot.settings"), legacy: role("Viewer") },
  { method: "POST", path: "/copilot/prompts", classify: mod("copilot.settings"), legacy: role("Viewer") },
  { method: "PATCH", path: "/copilot/prompts/:id", classify: mod("copilot.settings"), legacy: role("Viewer") },
  { method: "DELETE", path: "/copilot/prompts/:id", classify: mod("copilot.settings"), legacy: role("Viewer") },
  // The agents a chat may run under. Ordinary Copilot use, like the saved
  // prompts above it: choosing one is not administration, and MAINTAINING the
  // deployment's set is — which is why the four thinking-model routes that used
  // to sit here went with migration 0059, leaving /agent-admin as the only way
  // to write one.
  { method: "GET", path: "/copilot/agents", classify: mod("copilot.chat"), legacy: role("Viewer") },

  // ══ Internal AI agents ═══════════════════════════════════════════════════
  // Active catalogue, personal conversation history and chat are ordinary
  // Copilot use. Global definitions, knowledge, webhook contracts and logs are
  // deployment administration and therefore use settings read/update.
  { method: "GET", path: "/agents", classify: mod("copilot.chat"), legacy: role("Viewer") },
  { method: "GET", path: "/agents/:id", classify: mod("copilot.chat"), legacy: role("Viewer") },
  { method: "GET", path: "/agents/:id/conversations", classify: mod("copilot.history"), legacy: role("Viewer") },
  { method: "GET", path: "/agents/conversations/:id", classify: mod("copilot.history"), legacy: role("Viewer") },
  { method: "GET", path: "/agent-admin/agents", classify: mod("settings.read"), legacy: role("Administrator") },
  { method: "POST", path: "/agent-admin/agents", classify: mod("settings.update"), legacy: role("Administrator") },
  { method: "GET", path: "/agent-admin/agents/:id", classify: mod("settings.read"), legacy: role("Administrator") },
  { method: "PATCH", path: "/agent-admin/agents/:id", classify: mod("settings.update"), legacy: role("Administrator") },
  { method: "GET", path: "/agent-admin/agents/:id/knowledge", classify: mod("settings.read"), legacy: role("Administrator") },
  { method: "POST", path: "/agent-admin/agents/:id/knowledge", classify: mod("settings.update"), legacy: role("Administrator") },
  { method: "POST", path: "/agent-admin/agents/:id/knowledge-file", classify: mod("settings.update"), legacy: role("Administrator") },
  { method: "DELETE", path: "/agent-admin/agents/:id/knowledge/:knowledgeId", classify: mod("settings.update"), legacy: role("Administrator") },
  { method: "GET", path: "/agent-admin/agents/:id/tools", classify: mod("settings.read"), legacy: role("Administrator") },
  { method: "POST", path: "/agent-admin/agents/:id/tools", classify: mod("settings.update"), legacy: role("Administrator") },
  { method: "PATCH", path: "/agent-admin/agents/:id/tools/:toolId", classify: mod("settings.update"), legacy: role("Administrator") },
  { method: "DELETE", path: "/agent-admin/agents/:id/tools/:toolId", classify: mod("settings.update"), legacy: role("Administrator") },
  { method: "GET", path: "/agent-admin/agents/:id/logs", classify: mod("settings.read"), legacy: role("Administrator") },

  // ══ M365 onboarding — human-facing ════════════════════════════════════════
  { method: "GET", path: "/browser-sessions", classify: mod("onboarding.read"), legacy: role("Operator") },
  { method: "GET", path: "/browser-sessions/:id", classify: mod("onboarding.read"), legacy: role("Operator") },
  { method: "GET", path: "/browser-sessions/:id/gdap", classify: mod("onboarding.read"), legacy: role("Operator") },
  { method: "POST", path: "/browser-sessions/launch-request", classify: mod("onboarding.session_launch"), legacy: role("Operator") },
  { method: "POST", path: "/browser-sessions/:id/close", classify: mod("onboarding.session_close"), legacy: role("Operator") },
  { method: "POST", path: "/browser-sessions/:id/relaunch", classify: mod("onboarding.session_launch"), legacy: role("Operator") },
  { method: "POST", path: "/browser-sessions/:id/partnership", classify: mod("onboarding.session_launch"), legacy: role("Operator") },
  { method: "POST", path: "/browser-sessions/:id/provision", classify: mod("onboarding.provision_app"), legacy: role("Operator") },
  { method: "GET", path: "/browser-sessions/:id/provision", classify: mod("onboarding.read_app_secret"), legacy: role("Operator") },
  { method: "POST", path: "/browser-sessions/:id/gdap", classify: mod("onboarding.gdap_assign"), legacy: role("Operator") },

  // ══ M365 onboarding — the browser agent's own callback surface ═════════════
  // Authorized as the agent principal, additionally bound to the one session it
  // was issued for. Never reachable by a human module level.
  { method: "POST", path: "/browser-sessions/:id/agent/consume", classify: { kind: "service", principal: "svc_browser_agent", action: "onboarding.agent_report" }, legacy: { kind: "service", principal: "browser agent session token" } },
  { method: "GET", path: "/browser-sessions/:id/agent/control", classify: { kind: "service", principal: "svc_browser_agent", action: "onboarding.agent_report" }, legacy: { kind: "service", principal: "browser agent session token" } },
  { method: "POST", path: "/browser-sessions/:id/agent/navigate", classify: { kind: "service", principal: "svc_browser_agent", action: "onboarding.agent_report" }, legacy: { kind: "service", principal: "browser agent session token" } },
  { method: "POST", path: "/browser-sessions/:id/agent/tenant", classify: { kind: "service", principal: "svc_browser_agent", action: "onboarding.agent_report" }, legacy: { kind: "service", principal: "browser agent session token" } },
  { method: "POST", path: "/browser-sessions/:id/agent/provision/started", classify: { kind: "service", principal: "svc_browser_agent", action: "onboarding.agent_report" }, legacy: { kind: "service", principal: "browser agent session token" } },
  { method: "POST", path: "/browser-sessions/:id/agent/provision/progress", classify: { kind: "service", principal: "svc_browser_agent", action: "onboarding.agent_report" }, legacy: { kind: "service", principal: "browser agent session token" } },
  { method: "POST", path: "/browser-sessions/:id/agent/provision/result", classify: { kind: "service", principal: "svc_browser_agent", action: "onboarding.agent_report" }, legacy: { kind: "service", principal: "browser agent session token" } },
  { method: "POST", path: "/browser-sessions/:id/agent/provision/failed", classify: { kind: "service", principal: "svc_browser_agent", action: "onboarding.agent_report" }, legacy: { kind: "service", principal: "browser agent session token" } },
  { method: "POST", path: "/browser-sessions/:id/agent/started", classify: { kind: "service", principal: "svc_browser_agent", action: "onboarding.agent_report" }, legacy: { kind: "service", principal: "browser agent session token" } },
  { method: "POST", path: "/browser-sessions/:id/agent/closed", classify: { kind: "service", principal: "svc_browser_agent", action: "onboarding.agent_report" }, legacy: { kind: "service", principal: "browser agent session token" } },
  { method: "POST", path: "/browser-sessions/:id/agent/failed", classify: { kind: "service", principal: "svc_browser_agent", action: "onboarding.agent_report" }, legacy: { kind: "service", principal: "browser agent session token" } },

  // ══ FortiGate ═════════════════════════════════════════════════════════════
  { method: "POST", path: "/fortigate/check", classify: mod("fortigate.check"), legacy: role("Operator") },
  // Stored sweep history is ordinary read data; the probes that produce it are
  // not. The legacy side stays Operator across the board — the whole FortiGate
  // page was Operator-gated before the programme existed, so mapping the new
  // reads to Viewer would report a divergence for a capability nobody had.
  { method: "GET", path: "/fortigate/sweeps", classify: mod("fortigate.read"), legacy: role("Operator") },
  { method: "GET", path: "/fortigate/sweeps/latest", classify: mod("fortigate.read"), legacy: role("Operator") },
  { method: "GET", path: "/fortigate/sweeps/:sweepId", classify: mod("fortigate.read"), legacy: role("Operator") },
  { method: "GET", path: "/fortigate/monitor/schedule", classify: mod("fortigate.read"), legacy: role("Operator") },
  { method: "POST", path: "/fortigate/sweeps", classify: mod("fortigate.sweep_run"), legacy: role("Operator") },
  // Operator notes on a fleet row (0039). Reading them is part of reading the
  // fleet; writing one is an edit to shared platform state, not an outbound probe.
  { method: "GET", path: "/fortigate/notes", classify: mod("fortigate.read"), legacy: role("Operator") },
  { method: "PUT", path: "/fortigate/notes", classify: mod("fortigate.note_write"), legacy: role("Operator") },
  { method: "PATCH", path: "/fortigate/monitor/schedule", classify: mod("fortigate.schedule_manage"), legacy: role("Operator") },
  { method: "POST", path: "/fortigate/monitor/report", classify: mod("fortigate.send_report"), legacy: role("Operator") },
  // The report preview renders the same HTML the email carries, without sending
  // it — a read, deliberately not gated behind the send permission.
  { method: "GET", path: "/fortigate/sweeps/:sweepId/report.html", classify: mod("fortigate.read"), legacy: role("Operator") },

  // ══ Webhooks ══════════════════════════════════════════════════════════════
  { method: "GET", path: "/webhooks", classify: mod("webhooks.read"), legacy: role("Viewer") },
  { method: "GET", path: "/webhooks/templates", classify: mod("webhooks.read"), legacy: role("Viewer") },
  { method: "GET", path: "/webhooks/deliveries", classify: mod("webhooks.read"), legacy: role("Viewer") },
  { method: "POST", path: "/webhooks/preview", classify: mod("webhooks.update"), legacy: role("Operator") },
  { method: "POST", path: "/webhooks/test", classify: mod("webhooks.test"), legacy: role("Operator") },
  { method: "POST", path: "/webhooks", classify: mod("webhooks.create"), legacy: role("Operator") },
  { method: "PUT", path: "/webhooks/:id", classify: mod("webhooks.update"), legacy: role("Operator") },
  { method: "POST", path: "/webhooks/:id/enable", classify: mod("webhooks.toggle"), legacy: role("Operator") },
  { method: "POST", path: "/webhooks/:id/disable", classify: mod("webhooks.toggle"), legacy: role("Operator") },
  { method: "POST", path: "/webhooks/:id/test", classify: mod("webhooks.test"), legacy: role("Operator") },
  { method: "POST", path: "/webhooks/deliveries/:id/replay", classify: mod("webhooks.replay"), legacy: role("Operator") },
  { method: "DELETE", path: "/webhooks/:id", classify: mod("webhooks.delete"), legacy: role("Administrator") },

  // ══ Users ═════════════════════════════════════════════════════════════════
  { method: "GET", path: "/users", classify: mod("users.read"), legacy: perm("users.read") },
  { method: "POST", path: "/users", classify: mod("users.create"), legacy: perm("users.create") },
  { method: "GET", path: "/users/:id", classify: mod("users.read"), legacy: perm("users.read") },
  { method: "PATCH", path: "/users/:id", classify: mod("users.update"), legacy: perm("users.update") },
  {
    method: "PUT",
    path: "/users/:id/status",
    classify: mod("users.disable"),
    legacy: perm("users.update"),
    note:
      "Split out of PATCH /users/:id: changing lifecycle status is a different " +
      "authorization decision from editing a display name, and one endpoint " +
      "must correspond to one decision.",
  },
  { method: "DELETE", path: "/users/:id", classify: mod("users.delete"), legacy: perm("users.delete") },
  { method: "GET", path: "/users/:id/history", classify: mod("audit.read"), legacy: perm("audit.read") },
  { method: "POST", path: "/users/:id/roles", classify: mod("authorization.assign_roles"), legacy: perm("roles.assign") },
  { method: "DELETE", path: "/users/:id/roles/:roleId", classify: mod("authorization.assign_roles"), legacy: perm("roles.assign") },
  { method: "GET", path: "/users/:id/sessions", classify: mod("sessions.read"), legacy: perm("sessions.read") },
  { method: "DELETE", path: "/users/:id/sessions/:sessionId", classify: mod("sessions.revoke"), legacy: perm("sessions.revoke") },
  { method: "DELETE", path: "/users/:id/sessions", classify: mod("sessions.revoke"), legacy: perm("sessions.revoke") },

  // ══ Roles & permissions ═══════════════════════════════════════════════════
  { method: "GET", path: "/roles", classify: mod("authorization.read"), legacy: perm("roles.read") },
  { method: "GET", path: "/roles/:id", classify: mod("authorization.read"), legacy: perm("roles.read") },
  { method: "GET", path: "/permissions", classify: mod("authorization.read"), legacy: perm("roles.read") },
  { method: "GET", path: "/authz/manifest", classify: mod("authorization.read"), legacy: perm("roles.read") },
  { method: "GET", path: "/users/:id/effective-access", classify: mod("authorization.read"), legacy: perm("roles.read") },
  { method: "POST", path: "/roles", classify: mod("authorization.role_create"), legacy: perm("roles.create") },
  { method: "POST", path: "/roles/:id/duplicate", classify: mod("authorization.role_create"), legacy: perm("roles.create") },
  { method: "PATCH", path: "/roles/:id", classify: mod("authorization.role_update"), legacy: perm("roles.update") },
  { method: "PUT", path: "/roles/:id/permissions", classify: mod("authorization.role_update"), legacy: perm("roles.update") },
  { method: "PUT", path: "/roles/:id/grants", classify: mod("authorization.role_update"), legacy: perm("roles.update") },
  { method: "DELETE", path: "/roles/:id", classify: mod("authorization.role_delete"), legacy: perm("roles.delete") },

  // ══ Audit log ═════════════════════════════════════════════════════════════
  { method: "GET", path: "/audit", classify: mod("audit.read"), legacy: role("Viewer") },
  // The entity-kind filter's options. `audit.read`, matching /audit itself: it
  // returns the distinct entity_type values already visible in the rows the
  // caller can page through, so it discloses nothing the list does not.
  { method: "GET", path: "/audit/entity-types", classify: mod("audit.read"), legacy: role("Viewer") },
  { method: "GET", path: "/audit/export", classify: mod("audit.export"), legacy: role("Administrator") },
  // Retention. Reading the policy and its preview is `audit.read` — it reveals
  // only row counts. CHANGING it is its own special action, because it decides
  // when audit history stops existing.
  { method: "GET", path: "/audit/retention", classify: mod("audit.read"), legacy: role("Viewer") },
  { method: "PUT", path: "/audit/retention", classify: mod("audit.retention_manage"), legacy: role("Administrator") },
  {
    method: "GET",
    path: "/audit/authz-divergence",
    // The `audit.export` SPECIAL, not `audit.read`. The report is a bulk
    // extraction of the decision log — it names principals and the endpoints they
    // were denied — and the route was Administrator-gated. Mapping it to
    // `audit.read` would have handed it to every role that can page the trail on
    // screen, which is a widening the equivalence sweep caught.
    classify: mod("audit.export"),
    legacy: role("Administrator"),
    note: "Shadow-mode divergence report, used to decide when enforcement is safe.",
  },

  // ══ Settings ══════════════════════════════════════════════════════════════
  {
    method: "GET",
    path: "/settings/general",
    classify: mod("settings.read"),
    legacy: role("Viewer"),
    note:
      "No longer required for application-wide polling: the caller-scoped " +
      "refresh cadence moved to GET /me, so this is now purely the operational " +
      "settings read it always claimed to be.",
  },
  { method: "GET", path: "/settings", classify: mod("settings.read"), legacy: role("Administrator") },
  { method: "GET", path: "/settings/security", classify: mod("settings.read"), legacy: role("Administrator") },
  { method: "GET", path: "/settings/integrations", classify: mod("settings.read"), legacy: role("Administrator") },
  { method: "GET", path: "/settings/browser-agent", classify: mod("settings.read"), legacy: role("Administrator") },
  { method: "PUT", path: "/settings/general", classify: mod("settings.update"), legacy: role("Administrator") },
  { method: "GET", path: "/settings/branding", classify: mod("settings.read"), legacy: role("Administrator") },
  { method: "PUT", path: "/settings/branding", classify: mod("settings.update"), legacy: role("Administrator") },
  { method: "POST", path: "/settings/integrations/:id/test", classify: mod("settings.test_integration"), legacy: role("Administrator") },
  {
    method: "PUT",
    path: "/settings/authz-mode",
    classify: mod("settings.update"),
    legacy: role("Administrator"),
    note: "The rollout switch. Flipping to shadow is the no-deploy rollback.",
  },

  // ══ API keys ══════════════════════════════════════════════════════════════
  { method: "GET", path: "/api-keys/scopes", classify: mod("apikeys.read"), legacy: perm("settings.read") },
  { method: "GET", path: "/api-keys", classify: mod("apikeys.read"), legacy: perm("settings.read") },
  { method: "GET", path: "/api-keys/:id", classify: mod("apikeys.read"), legacy: perm("settings.read") },
  { method: "GET", path: "/api-keys/:id/events", classify: mod("apikeys.read_events"), legacy: perm("settings.read") },
  { method: "PATCH", path: "/api-keys/:id", classify: mod("apikeys.update"), legacy: perm("settings.update") },
  { method: "PUT", path: "/api-keys/:id/enabled", classify: mod("apikeys.toggle"), legacy: perm("settings.update") },
  { method: "PUT", path: "/api-keys/:id/scopes", classify: mod("apikeys.set_scopes"), legacy: perm("settings.update") },
  { method: "POST", path: "/api-keys", classify: mod("apikeys.create"), legacy: perm("settings.update") },
  { method: "POST", path: "/api-keys/:id/rotate", classify: mod("apikeys.rotate"), legacy: perm("settings.update") },
  { method: "DELETE", path: "/api-keys/:id", classify: mod("apikeys.revoke"), legacy: perm("settings.update") },

  // ══ API reference ═════════════════════════════════════════════════════════
  { method: "GET", path: "/docs", classify: mod("docs.read"), legacy: perm("settings.read") },
  { method: "GET", path: "/docs/openapi.json", classify: mod("docs.read"), legacy: perm("settings.read") },
  { method: "GET", path: "/docs/assets/:file", classify: mod("docs.read"), legacy: perm("settings.read") },
];

// ── Lookup ───────────────────────────────────────────────────────────────────

const key = (method: string, path: string): string => `${method.toUpperCase()} ${path}`;

const BY_ROUTE = new Map<string, EndpointMapping>(
  ENDPOINT_MAP.map((e) => [key(e.method, e.path), e]),
);

/** Exact method+path lookup. No prefix or normalization games. */
export function lookupEndpoint(
  method: string,
  path: string,
): EndpointMapping | undefined {
  return BY_ROUTE.get(key(method, path));
}

/** The module a mapped endpoint belongs to, when it requires one. */
export function endpointModule(mapping: EndpointMapping): ModuleKey | null {
  if (mapping.classify.kind !== "module") return null;
  return moduleForAction(mapping.classify.action)?.key ?? null;
}

// ── Self-validation ──────────────────────────────────────────────────────────

/**
 * Structural problems in the endpoint map itself: duplicate routes, and actions
 * that do not exist in the manifest. The route-coverage check (does every
 * REGISTERED route appear here?) needs a live Fastify instance and lives in the
 * API's authorization drift test.
 */
export function validateEndpointMap(): string[] {
  const problems: string[] = [];
  const seen = new Set<string>();

  for (const e of ENDPOINT_MAP) {
    const k = key(e.method, e.path);
    if (seen.has(k)) problems.push(`duplicate endpoint mapping '${k}'`);
    seen.add(k);

    if (!e.path.startsWith("/")) problems.push(`${k}: path must start with '/'`);
    if (e.path.includes("/v1")) {
      problems.push(`${k}: path must not include the '/v1' prefix`);
    }

    if (e.classify.kind === "module" && !isKnownAction(e.classify.action)) {
      problems.push(`${k}: unknown action '${e.classify.action}'`);
    }
    if (
      e.classify.kind === "service" &&
      e.classify.action &&
      !isKnownAction(e.classify.action)
    ) {
      problems.push(`${k}: unknown service action '${e.classify.action}'`);
    }
  }

  return problems;
}

export function assertEndpointMapValid(): void {
  const problems = validateEndpointMap();
  if (problems.length > 0) {
    throw new Error(
      `Invalid endpoint map:\n${problems.map((p) => `  - ${p}`).join("\n")}`,
    );
  }
}
